// The per-bee health/nectar micro-bars (bee-body-palette, playtest finding 10: "add bars to
// all bees that indicate their health and the nectar that they're carrying — this should go
// above the bee and follow them around"). Pure and Pixi-free, like `ground-scatter.ts` and
// `bee-palette.ts`: this decides WHICH bars a bee shows, how full each is, and where it sits
// relative to the bee's ground origin; `CanvasRenderer.drawBeeBars` batches the actual paint
// into a single `Graphics` (never one per bee — the endgame is thousands of close-tier bees)
// and is device-exercised only (repo convention D-086).

/** The bars' on-screen width (px) at depth scale 1 — a little narrower than the 24 px
 * directional sprite cell, so a bar reads as belonging to the body beneath it rather than
 * overhanging it. */
export const BEE_BAR_WIDTH_PX = 14;

/** One bar's on-screen height (px) at depth scale 1.
 *
 * Raised 2 → 3 px (2026-08-11) because the health bar gained a three-state colour ramp. At 2 px
 * this module's own header argued — correctly — that a hue ramp would not read; 3 px is the height
 * at which it does, and it is still under the 24 px sprite it belongs to. The camera-scale floor
 * below (`BEE_BAR_MIN_CAMERA_SCALE`) is what keeps the taller bars from smearing into a band when
 * zoomed out; that guard predates this and is unchanged. */
export const BEE_BAR_HEIGHT_PX = 3;

/** Vertical gap (px) between the two stacked bars. */
export const BEE_BAR_GAP_PX = 1;

/** How far above the bee's ground origin (px, at depth scale 1) the TOP bar's top edge sits.
 * The bee sprite is bottom-anchored and 24 px tall and a flying bee hovers a few px above
 * that (`BASE_HOVER` + a ±1.5 px bob), so 32 clears the tallest bee at the maximum depth
 * scale without floating so far up that the bars stop reading as the bee's own. */
export const BEE_BAR_RISE_PX = 32;

/** Health-bar fill above {@link BEE_BAR_HEALTH_AMBER_BELOW} — a **brighter** green than the kit's
 * `--success` (`#5F8A34`), which was chosen to sit on paper chrome and reads as barely-not-the-grass
 * over the season field (2026-08-11 finding: "hard to read due to the mostly green background"). */
export const BEE_BAR_HEALTH_COLOR = 0x46d838; // --bee-bar-health

/** Health-bar fill below 50% — `--honey-500`, the kit's amber. Deliberately the honey value rather
 * than `--warning` (`#9C5F0A`), which is a dark brown-gold and does not read as amber at bar size. */
export const BEE_BAR_HEALTH_AMBER_COLOR = 0xe8a020; // --bee-bar-health-amber

/** Health-bar fill below 25%.
 *
 * **Not `--danger` (`#C4472E`)**: `ui/tokens/colors.css` records that `--danger` is byte-identical
 * to `--colony-1` and `--bee-warrior`, so painting it onto a per-bee overlay "briefly asserts a
 * false colony identity". This is a distinct, brighter red that belongs to no colony palette. */
export const BEE_BAR_HEALTH_RED_COLOR = 0xf5261a; // --bee-bar-health-red

/** Health fractions at or below which the fill switches colour. */
export const BEE_BAR_HEALTH_AMBER_BELOW = 0.5;
export const BEE_BAR_HEALTH_RED_BELOW = 0.25;

/** Nectar-bar fill (`--honey-500`) — the game's own nectar colour. */
export const BEE_BAR_NECTAR_COLOR = 0xe8a020;

/** Both bars' unfilled track (`--ink-1`, painted at {@link BEE_BAR_TRACK_ALPHA}), so a
 * near-empty bar still shows its extent rather than vanishing. */
export const BEE_BAR_TRACK_COLOR = 0x2b2114;

/** The track's alpha. Raised 0.35 → 0.8 (2026-08-11): at 0.35 the warm field showed through and the
 * track read as a smudge rather than as the bar's dark ground. */
export const BEE_BAR_TRACK_ALPHA = 0.8;

/** The health bar's outline colour and width (screen px, pre-scale).
 *
 * The health bar alone is stroked, and that asymmetry is the point: it both lifts the bar off the
 * green field AND is what distinguishes health from the honey-coloured nectar bar once health has
 * gone amber, at which moment the two fills are nearly the same colour. Stroking both would spend
 * the distinguishing signal to gain nothing. */
export const BEE_BAR_HEALTH_OUTLINE_COLOR = 0xffffff; // --bee-bar-health-outline
export const BEE_BAR_HEALTH_OUTLINE_WIDTH_PX = 1;

/**
 * The fill colour for a health bar at `fill` (0..1): green, amber below 50%, red below 25%.
 *
 * Thresholds are `<`, so exactly 50% is still green and exactly 25% still amber — a bar reads as
 * the worse state only once it is genuinely past the line.
 */
export function healthBarColor(fill: number): number {
  if (fill < BEE_BAR_HEALTH_RED_BELOW) return BEE_BAR_HEALTH_RED_COLOR;
  if (fill < BEE_BAR_HEALTH_AMBER_BELOW) return BEE_BAR_HEALTH_AMBER_COLOR;
  return BEE_BAR_HEALTH_COLOR;
}

/**
 * The camera scale below which bars are suppressed — the **close-tier zoom floor**
 * (`defaultTierForZoom`'s Close→Wide hysteresis edge, `ui/camera/camera-gestures.ts`), pinned
 * to it by test rather than by an import so neither module depends on the other.
 *
 * The floor is the right cut because below it the subscription is Wide and there are no
 * individual bees to bar at all. The frames this actually protects are the **lag window**: a
 * zoom-out gesture debounces, resubscribes, and waits for a confirming Wide keyframe, and
 * until it lands the renderer keeps drawing close bees at an ever-lower camera scale. Bees and
 * their bars both shrink with the camera now (`spriteCameraScale`), so as the scale falls they pack
 * together on screen and the bars smear into a continuous band — which is when they stop
 * being a readout. (An earlier attempt derived this from an assumed world-space bee spacing
 * and landed at 0.7, comfortably below the tier floor and therefore dead code.)
 */
export const BEE_BAR_MIN_CAMERA_SCALE = 0.9;

/** Whether bee bars are legible at this camera scale (monotone in `scale`, so a zoom sweep
 * never flickers them). A degenerate camera — non-finite, zero, or negative scale — hides
 * them rather than dividing by it. */
export function beeBarsVisibleAtScale(scale: number): boolean {
  if (!Number.isFinite(scale) || scale <= 0) return false;
  return scale >= BEE_BAR_MIN_CAMERA_SCALE;
}

export type BeeBarKind = 'health' | 'nectar';

/** One bar above one bee. */
export interface BeeBar {
  readonly kind: BeeBarKind;
  /** Filled fraction of the track, 0..1. */
  readonly fill: number;
  /** The bar's top edge in screen px relative to the bee's ground origin, at depth scale 1 —
   * negative, since screen y grows downward and the bars sit above the bee. The caller
   * multiplies by the bee's depth scale. */
  readonly offsetY: number;
}

/** The health bar's slot: the top one. */
const HEALTH_OFFSET_Y = -BEE_BAR_RISE_PX;
/** The nectar bar's slot: directly under the health bar. Both slots are FIXED — a bar never
 * moves because the other one appeared or vanished, which would read as jitter. */
const NECTAR_OFFSET_Y = -BEE_BAR_RISE_PX + BEE_BAR_HEIGHT_PX + BEE_BAR_GAP_PX;

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

/**
 * The bars one bee draws, from its raw wire bytes.
 *
 * `health` and `nectar` are quantized as a fraction of THIS bee's own max health / capacity
 * (buff-aware, derived server-side — `waggle-server/src/streaming/close.rs`), so the only
 * correct denominator is **255**, never a balance constant: `0` is exactly dead/empty and
 * `255` exactly full.
 *
 * A bar that carries no information is suppressed, so a healthy field stays visually quiet
 * and only the bees worth looking at are marked: a **full** health bar (the overwhelmingly
 * common case — a field of them is noise) and an **empty** nectar bar (likewise) are both
 * dropped. What is left reads directly as "who is hurt" and "who is laden".
 */
export function beeBars(health: number, nectar: number): BeeBar[] {
  const bars: BeeBar[] = [];
  if (health < 255) {
    bars.push({ kind: 'health', fill: clamp01(health / 255), offsetY: HEALTH_OFFSET_Y });
  }
  if (nectar > 0) {
    bars.push({ kind: 'nectar', fill: clamp01(nectar / 255), offsetY: NECTAR_OFFSET_Y });
  }
  return bars;
}
